import { SEAT_SIZE } from './ui/furnitureSpots.mjs';

export const API_URL = import.meta.env.VITE_API_URL ?? '';

export const WS_URL = (import.meta.env.VITE_WS_URL)
  ?? (API_URL
        ? API_URL.replace(/^http/, 'ws') + '/ws'
        : `${location.origin.replace(/^http/, 'ws')}/ws`);

// Cache-busting for static assets — see assetVersion.js (kept separate so it's
// importable from plain Node without config.js's import.meta.env/location at load).
export { ASSET_VERSION, assetUrl } from './assetVersion.js';
import { BIKE_SPEED } from './ui/bikeRide.mjs';

// Street geometry (door centres, world width) — Phaser-free so it also loads under
// `node --test`; see streetSpawns.mjs.
export {
  STREET_SPAWNS, STREET_SPAWN_Y, STREET_WORLD_WIDTH, STREET_DOORS,
  STREET_SRC_W, STREET_SRC_H, STREET_SCALE, STREET_TILES,
  STREET_FEET, STREET_FEET_Y, STREET_WALK_CENTER_Y, STREET_RIDE,
  STREET_SIDEWALK_TOP_Y, STREET_SIDEWALK_KERB_Y, STREET_ASPHALT_TOP_Y, STREET_ASPHALT_BOTTOM_Y, STREET_KERB_FRONT_Y,
} from './streetSpawns.mjs';

export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

export const SCENE = {
  BOOT: 'BootScene',
  LOGIN: 'LoginScene',
  PRELOAD: 'PreloadScene',
  MAIN: 'MainScene',
  HOME: 'HomeScene',
  FARM: 'FarmScene',
  SHOP: 'ShopScene',
  OUTSIDE: 'OutsideScene',
  KITCHEN: 'KitchenScene',
  SALON: 'SalonScene',
  SECOND_FLOOR: 'SecondFloorScene',
  // Sub-project A
  PET_SHOP: 'PetShopScene',
  FURNITURE_SHOP: 'FurnitureShopScene',
  CLOTHING_SHOP: 'ClothingShopScene',
  BIKE_SHOP: 'BikeShopScene',
  BACKYARD: 'BackyardScene',
  // Driving (spec 2026-08-27)
  DRIVING_SCHOOL: 'DrivingSchoolScene',
  CAR_DEALER: 'CarDealerScene',        // reserved — scene arrives with the cars spec
  TRAINING_ROUTE: 'TrainingRouteScene', // practical lesson/exam driving (spec 3, Task 6)
  // Game Hall (Salon Gier)
  GAME_HALL: 'GameHallScene',
  GAME_HALL_UP: 'GameHallUpstairsScene',
  BOARD_GAME: 'BoardGameScene',
  // Read-only visitor scenes (Task 15)
  VISIT_MAIN: 'VisitMainScene',
  VISIT_HOME: 'VisitHomeScene',
  VISIT_KITCHEN: 'VisitKitchenScene',
  VISIT_SALON: 'VisitSalonScene',
  VISIT_SECOND_FLOOR: 'VisitSecondFloorScene',
  VISIT_FARM: 'VisitFarmScene',
  VISIT_BACKYARD: 'VisitBackyardScene',
};

// Bike (rowerek) tuning. The speeds live in ui/bikeRide.mjs (Phaser-free, so its tests can read them).
export const BIKE = {
  TYPES: {
    pink: { SPEED_GREASED: BIKE_SPEED.pink },     // px/s while the chain is greased
    blue: { SPEED_GREASED: BIKE_SPEED.blue },     // the faster premium bike
  },
  SPEED_DRY: BIKE_SPEED.dry,  // px/s with a dry chain (same as walking) — any bike
  GREASE_DRAIN_PER_SEC: 1.5,// grease units burned per second of moving
  GREASE_SYNC_INTERVAL: 3,  // seconds between throttled server grease syncs
  NEAR_DISTANCE: 160,       // how close (px) the walker must be to mount a parked bike
  RIDER_W: 240,
  RIDER_H: 240,
  PARKED_W: 200,
  PARKED_H: 200,
};

export const PHASE_COLORS = {
  night:   0x0f1027,
  morning: 0xffb6c1,
  day:     0x87ceeb,
  evening: 0xffa07a,
};

// Maps Phaser scene keys to backend "scene" string in placement APIs.
export const SCENE_TO_BACKEND = {
  [SCENE.HOME]:         'home',
  [SCENE.FARM]:         'farm',
  [SCENE.KITCHEN]:      'kitchen',
  [SCENE.SALON]:        'salon',
  [SCENE.SECOND_FLOOR]: 'second_floor',
  [SCENE.BACKYARD]:     'backyard',   // side yard — decorations only
  [SCENE.MAIN]:         'exterior',   // front yard — decorations only
};

export const BACKEND_TO_SCENE = Object.fromEntries(
  Object.entries(SCENE_TO_BACKEND).map(([k, v]) => [v, k])
);

// Furniture may only be placed indoors — never on the farm.
// Mirrors FURNITURE_SCENES on the backend (catalog.py).
export const FURNITURE_SCENES = new Set(['home', 'kitchen', 'salon', 'second_floor']);

// Scenes a refresh is allowed to resume into (see locationTracker + BootScene).
// Excludes BOOT/LOGIN/MAIN-is-the-fallback. MAIN is included as a valid resume too.
export const RESTORABLE_SCENES = new Set([
  SCENE.MAIN, SCENE.OUTSIDE, SCENE.HOME, SCENE.FARM, SCENE.KITCHEN, SCENE.SALON,
  SCENE.SECOND_FLOOR, SCENE.SHOP, SCENE.PET_SHOP, SCENE.FURNITURE_SHOP,
  SCENE.CLOTHING_SHOP, SCENE.BIKE_SHOP, SCENE.BACKYARD,
  SCENE.GAME_HALL, SCENE.GAME_HALL_UP, SCENE.DRIVING_SCHOOL, SCENE.CAR_DEALER,
]);

// A friend's place: not saved on the server, so a new-version reload remembers it itself (ui/autoUpdate.js).
export const VISIT_SCENES = new Set([
  SCENE.VISIT_MAIN, SCENE.VISIT_HOME, SCENE.VISIT_KITCHEN, SCENE.VISIT_SALON,
  SCENE.VISIT_SECOND_FLOOR, SCENE.VISIT_FARM, SCENE.VISIT_BACKYARD,
]);

// Placements that may be mirrored left/right ("Odwróć") — mirror of backend catalog.FLIPPABLE.
// Menu visibility only; the server validates. Symmetric art and the clock are excluded.
export const FLIPPABLE = {
  furniture: new Set(['lampa', 'obraz', 'szafa', ...Object.keys(SEAT_SIZE)]),   // every sofa and bed
  buda: new Set(['kurnik', 'obora', 'buda']),
  decoration: new Set(['roslina', 'balony', 'girlanda', 'plakat', 'lawka', 'skrzynka_kwiaty', 'posag']),
};
export function isFlippable(p) {
  return !!p && FLIPPABLE[p.type]?.has(p.item);
}


// Display size (px) for placed furniture/buda sprites. A few large pieces render
// at double size, a few small ones get a per-item override; everything else uses the default.
export const PLACEMENT_SIZE = 120;
const LARGE_FURNITURE = new Set(['szafa', 'lampa']);
const FURNITURE_SIZE_OVERRIDE = { zegar: 100, ...SEAT_SIZE };   // sofas 360, beds 300 (furnitureSpots.mjs)
export function placementSize(item) {
  if (item in FURNITURE_SIZE_OVERRIDE) return FURNITURE_SIZE_OVERRIDE[item];
  return LARGE_FURNITURE.has(item) ? PLACEMENT_SIZE * 2 : PLACEMENT_SIZE;
}
